const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const toFiniteEpoch = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized >= 0 ? normalized : null;
};

const normalizeReadinessState = (value) => {
  const normalized = toStringOrNull(value);
  return normalized ? normalized.toLowerCase() : null;
};

const shallowEqualRecord = (left = null, right = null) => {
  if (left === right) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every(
    (key) => Object.prototype.hasOwnProperty.call(right, key) && Object.is(left[key], right[key]),
  );
};

const resolveServerReadinessState = (group = null) => {
  if (!group || typeof group !== 'object') {
    return null;
  }

  return normalizeReadinessState(
    group?.serverReadinessState ??
      group?.server_readiness_state ??
      group?.currentDevice?.state ??
      group?.current_device?.state ??
      group?.readinessState ??
      group?.readiness_state ??
      null,
  );
};

const resolveServerSendCapability = (group = null) => {
  if (!group || typeof group !== 'object') return null;
  const value =
    group?.serverSendCapable ??
    group?.server_send_capable ??
    group?.currentDevice?.sendCapable ??
    group?.currentDevice?.send_capable ??
    group?.current_device?.send_capable ??
    null;
  return typeof value === 'boolean' ? value : null;
};

export const isMessengerV2CurrentDeviceEnrollmentRequired = (
  group = null,
  { clientId = null } = {},
) => {
  if (!group || typeof group !== 'object') return false;
  const currentDevice =
    group?.currentDevice && typeof group.currentDevice === 'object'
      ? group.currentDevice
      : group?.current_device && typeof group.current_device === 'object'
        ? group.current_device
        : null;
  if (!currentDevice) return false;

  const expectedClientId = toStringOrNull(clientId);
  const currentDeviceClientId = toStringOrNull(
    currentDevice?.clientId ?? currentDevice?.client_id ?? null,
  );
  if (expectedClientId && currentDeviceClientId && expectedClientId !== currentDeviceClientId) {
    return false;
  }

  if (currentDevice?.enrolled === false) return true;
  const reason = toStringOrNull(currentDevice?.reason)?.toLowerCase() || null;
  return (
    currentDevice?.sendCapable !== true &&
    currentDevice?.send_capable !== true &&
    ['current_device_enrollment_pending', 'current_device_key_package_required'].includes(reason)
  );
};

export const isMessengerV2ReadOnlyGroup = (group = null) => {
  if (!group || typeof group !== 'object') {
    return false;
  }

  const readinessState = resolveServerReadinessState(group);
  const readinessReason = toStringOrNull(group?.readinessReason ?? group?.readiness_reason ?? null);

  return (
    group?.readOnly === true ||
    group?.read_only === true ||
    readinessState === 'read_only' ||
    readinessReason === 'former_member'
  );
};

const isServerBlocked = (group = null) => {
  const readinessState = resolveServerReadinessState(group);
  if (readinessState === 'blocked') {
    return true;
  }
  return group?.recoveryBlocked === true || group?.recovery_blocked === true;
};

export const hasMessengerV2ConcreteRuntime = (group = null, { clientId = null } = {}) => {
  if (!group || typeof group !== 'object') {
    return false;
  }
  if (group?.runtimeLive === false || group?.runtime_live === false) {
    return false;
  }
  if (isMessengerV2CurrentDeviceEnrollmentRequired(group, { clientId })) {
    return false;
  }

  const runtimeHandle = toStringOrNull(
    group?.runtimeHandle ??
      group?.runtime_handle ??
      group?.groupHandle ??
      group?.group_handle ??
      null,
  );
  const epoch = toFiniteEpoch(group?.epoch ?? group?.current_epoch ?? null);
  const runtimeEpoch = toFiniteEpoch(group?.runtimeEpoch ?? group?.runtime_epoch ?? null);
  const sessionGeneration = toFiniteEpoch(
    group?.sessionGeneration ?? group?.session_generation ?? null,
  );
  const runtimeSessionGeneration = toFiniteEpoch(
    group?.runtimeSessionGeneration ?? group?.runtime_session_generation ?? null,
  );
  const expectedClientId = toStringOrNull(clientId);
  const currentDevice =
    group?.currentDevice && typeof group.currentDevice === 'object'
      ? group.currentDevice
      : group?.current_device && typeof group.current_device === 'object'
        ? group.current_device
        : null;
  const currentDeviceClientId = toStringOrNull(
    currentDevice?.clientId ?? currentDevice?.client_id ?? null,
  );
  const generationAligned =
    sessionGeneration == null ||
    runtimeSessionGeneration == null ||
    runtimeSessionGeneration === sessionGeneration;
  const clientAligned =
    !expectedClientId || !currentDeviceClientId || currentDeviceClientId === expectedClientId;

  return Boolean(
    runtimeHandle &&
      epoch != null &&
      runtimeEpoch != null &&
      runtimeEpoch === epoch &&
      generationAligned &&
      clientAligned,
  );
};

export const resolveMessengerV2HandshakeReplayEpoch = (group = null) => {
  if (!group || typeof group !== 'object' || isMessengerV2ReadOnlyGroup(group)) {
    return null;
  }

  const runtimeHandle = toStringOrNull(
    group?.runtimeHandle ??
      group?.runtime_handle ??
      group?.groupHandle ??
      group?.group_handle ??
      null,
  );
  const authoritativeEpoch = toFiniteEpoch(group?.epoch ?? group?.current_epoch ?? null);
  const runtimeEpoch = toFiniteEpoch(group?.runtimeEpoch ?? group?.runtime_epoch ?? null);

  return runtimeHandle &&
    runtimeEpoch != null &&
    runtimeEpoch >= 1 &&
    authoritativeEpoch != null &&
    runtimeEpoch < authoritativeEpoch
    ? runtimeEpoch
    : null;
};

export const isMessengerV2SendCapableGroup = (group = null, { clientId = null } = {}) => {
  if (!group || typeof group !== 'object') {
    return false;
  }
  if (group.needsRecovery) {
    return false;
  }
  if (isServerBlocked(group)) {
    return false;
  }
  if (isMessengerV2ReadOnlyGroup(group)) {
    return false;
  }
  const serverReadinessState = resolveServerReadinessState(group);
  if (serverReadinessState && serverReadinessState !== 'ready') {
    return false;
  }
  if (resolveServerSendCapability(group) === false) {
    return false;
  }

  return hasMessengerV2ConcreteRuntime(group, { clientId });
};

export const normalizeMessengerV2ReadyInfo = (readyInfo = null, ready = false) => {
  if (!readyInfo || typeof readyInfo !== 'object') {
    return ready ? { ready: true, sendCapable: true, send_capable: true } : null;
  }

  const serverReadinessState = resolveServerReadinessState(readyInfo);
  const serverSendCapability = resolveServerSendCapability(readyInfo);
  const readOnly = isMessengerV2ReadOnlyGroup(readyInfo);
  const blocked = isServerBlocked(readyInfo);
  const concreteRuntime = hasMessengerV2ConcreteRuntime(readyInfo);
  const serverAllowsSend =
    serverSendCapability !== false && (!serverReadinessState || serverReadinessState === 'ready');
  const sendCapable = !readOnly && !blocked && serverAllowsSend && concreteRuntime;
  const nextReady = sendCapable;
  const nextReadinessState = blocked
    ? 'blocked'
    : readOnly
      ? 'read_only'
      : nextReady
        ? 'ready'
        : serverReadinessState === 'pending_setup'
          ? 'pending_setup'
          : 'pending';
  const nextReadinessReason = nextReady
    ? null
    : (readyInfo?.readinessReason ??
      readyInfo?.readiness_reason ??
      (blocked ? 'recovery_blocked' : readOnly ? 'former_member' : 'runtime_missing'));
  const normalizedReadyInfo = {
    ...readyInfo,
    serverReadinessState,
    server_readiness_state: serverReadinessState,
    serverSendCapable: serverSendCapability,
    server_send_capable: serverSendCapability,
    ready: nextReady,
    readOnly,
    read_only: readOnly,
    sendCapable,
    send_capable: sendCapable,
    readyToSend: sendCapable,
    ready_to_send: sendCapable,
    readinessState: nextReadinessState,
    readiness_state: nextReadinessState,
    readinessReason: nextReadinessReason,
    readiness_reason: nextReadinessReason,
  };
  return shallowEqualRecord(readyInfo, normalizedReadyInfo) ? readyInfo : normalizedReadyInfo;
};
